import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { cajaService, Caja, CajaCreate, CajaUpdate } from '../../services/cajaService';
import { sucursalService } from '../../services/sucursalService';

interface CajaFormProps {
  caja?: Caja | null;
  onClose: () => void;
}

const CajaForm = ({ caja, onClose }: CajaFormProps) => {
  const queryClient = useQueryClient();

  const { data: sucursalesData } = useQuery(
    'sucursales',
    () => sucursalService.getAll(1, 100)
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<CajaCreate | CajaUpdate>({
    defaultValues: caja || {
      sucursalId: undefined,
      name: '',
      numeroCaja: 1
    }
  });

  useEffect(() => {
    if (caja) {
      reset(caja);
    }
  }, [caja, reset]);

  const createMutation = useMutation(cajaService.create, {
    onSuccess: () => {
      toast.success('Caja creada correctamente');
      queryClient.invalidateQueries(['cajas']);
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Error al crear caja');
    }
  });

  const updateMutation = useMutation(
    (data: CajaUpdate) => cajaService.update(caja!.id, data),
    {
      onSuccess: () => {
        toast.success('Caja actualizada correctamente');
        queryClient.invalidateQueries(['cajas']);
        onClose();
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Error al actualizar caja');
      }
    }
  );

  const onSubmit = (data: CajaCreate | CajaUpdate) => {
    if (caja) {
      updateMutation.mutate(data as CajaUpdate);
    } else {
      // Validar que sucursalId sea válido antes de enviar
      if (!data.sucursalId || data.sucursalId === 0) {
        toast.error('Por favor selecciona una sucursal');
        return;
      }
      createMutation.mutate(data as CajaCreate);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h2 className="text-xl font-bold mb-4">
          {caja ? 'Editar Caja' : 'Nueva Caja'}
        </h2>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sucursal *
            </label>
            <select
              {...register('sucursalId', { 
                required: 'Sucursal es requerida',
                valueAsNumber: true,
                validate: (value) => value > 0 || 'Sucursal es requerida'
              })}
              disabled={!!caja}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100"
            >
              <option value="">Selecciona una sucursal</option>
              {sucursalesData?.data.sucursales.map((sucursal) => (
                <option key={sucursal.id} value={sucursal.id}>
                  {sucursal.name}
                </option>
              ))}
            </select>
            {errors.sucursalId && (
              <p className="text-red-500 text-sm mt-1">{errors.sucursalId.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nombre *
            </label>
            <input
              {...register('name', { required: 'Nombre es requerido' })}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {errors.name && (
              <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Número de Caja *
            </label>
            <input
              type="number"
              min="1"
              {...register('numeroCaja', { 
                required: 'Número de caja es requerido',
                valueAsNumber: true,
                min: { value: 1, message: 'Debe ser mayor a 0' }
              })}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {errors.numeroCaja && (
              <p className="text-red-500 text-sm mt-1">{errors.numeroCaja.message}</p>
            )}
          </div>

          {caja && (
            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  {...register('isActive')}
                  className="rounded"
                />
                <span className="text-sm font-medium text-gray-700">Activa</span>
              </label>
            </div>
          )}

          <div className="flex justify-end space-x-2 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={createMutation.isLoading || updateMutation.isLoading}
              className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
            >
              {createMutation.isLoading || updateMutation.isLoading ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CajaForm;

